<template>
  <div class="shape-tap-game">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <h1 class="instruction">
        Tap the {{ targetShape }}
      </h1>
      <div class="grid">
        <div
          v-for="(shape, index) in displayedShapes"
          :key="index"
          class="shape-hole"
          @click="handleClick(shape, index)"
        >
          <div
            v-if="shape"
            :class="['shape', shape.toLowerCase()]"
            :style="getShapeStyle(shape, index)"
          />
        </div>
      </div>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <div class="progress">
        <p
          v-for="(count, shape) in shapeCounts"
          :key="shape"
        >
          {{ shape }}: {{ count }}/{{ maxCount }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      shapesList: ["Circle", "Square", "Triangle", "Rectangle", "Pentagon"],
      targetShape: "",
      displayedShapes: Array(9).fill(null), // A grid of 9 items
      shapeCounts: {},
      maxCount: 3,
      isGameComplete: false,
      message: "",
      intervalId: null,
      shapeColors: {
        Circle: "#FF5733",
        Square: "#00FFFF",
        Triangle: "#3357FF",
        Rectangle: "#FF33A1",
        Pentagon: "#A133FF",
      },
      correctClickIndex: null,
      selectedVoice: null,
    };
  },
  mounted() {
    this.initializeShapeCounts();
    this.setupGame();
    window.speechSynthesis.onvoiceschanged = this.setupVoices;
    this.setupVoices();
  },
  beforeUnmount() {
    clearInterval(this.intervalId);
  },
  methods: {
    initializeShapeCounts() {
      this.shapeCounts = this.shapesList.reduce((acc, shape) => {
        acc[shape] = 0;
        return acc;
      }, {});
    },
    setupGame() {
      this.setTargetShape();
      this.intervalId = setInterval(this.updateShapes, 1500);
    },
    setTargetShape() {
      this.targetShape = this.shapesList.find(
        (shape) => this.shapeCounts[shape] < this.maxCount
      );

      if (this.targetShape) this.speakOutLoud(`Tap the ${this.targetShape}!`);

      if (!this.targetShape) {
        this.isGameComplete = true;
        this.playSound(
          "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
        );
        this.speakOutLoud("Great Job!");
        clearInterval(this.intervalId);
        // Navigate to the desired route after completion
        
        setTimeout(() => {
          this.$router.push('/shapes');
        }, 4000);
        return;
      }
    },
    setupVoices() {
      const voices = window.speechSynthesis.getVoices();
      this.selectedVoice = voices[0];
    },
    updateShapes() {
      this.displayedShapes = this.displayedShapes.map(() => {
        const randomShape =
          this.shapesList[Math.floor(Math.random() * this.shapesList.length)];
        return Math.random() > 0.5 ? randomShape : null;
      });

      // Ensure that targetShape appears at least once
      const randomIndex = Math.floor(
        Math.random() * this.displayedShapes.length
      );
      this.displayedShapes[randomIndex] = this.targetShape;
      this.correctClickIndex = null;
    },
    handleClick(shape, index) {
      if (shape === this.targetShape) {
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.shapeCounts[shape]++;
        this.correctClickIndex = index;
        this.message = "✅";
        if (this.shapeCounts[shape] >= this.maxCount) {
          this.setTargetShape();
        }
      } else {
        this.message = "❌";
      }
      setTimeout(() => {
        this.message = "";
        this.correctClickIndex = null;
      }, 1000);
    },
    getShapeStyle(shape, index) {
      const isCorrectClick = this.correctClickIndex === index;
      const baseStyle = {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      };
      if (!shape) {
        return {};
      }
      const color = this.shapeColors[shape];
      let style = {};

      if (shape === "Circle") {
        style = {
          backgroundColor: color,
          borderRadius: "50%",
          width: "60px",
          height: "60px",
        };
      } else if (shape === "Square") {
        style = {
          backgroundColor: color,
          width: "60px",
          height: "60px",
        };
      } else if (shape === "Triangle") {
        style = {
          width: "0",
          height: "0",
          borderLeft: "30px solid transparent",
          borderRight: "30px solid transparent",
          borderBottom: `60px solid ${color}`,
        };
      } else if (shape === "Rectangle") {
        style = {
          backgroundColor: color,
          width: "80px",
          height: "40px",
        };
      } else if (shape === "Pentagon") {
        style = {
          backgroundColor: color,
          width: "60px",
          height: "60px",
          clipPath: "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)",
        };
      }

      if (isCorrectClick) {
        style.backgroundColor = "lightgreen";
      }

      return { ...baseStyle, ...style };
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
  },
};
</script>

<style scoped>
.shape-tap-game {
  text-align: center;
}

.grid {
  max-width: 400px;
  margin: auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.shape-hole {
  width: 100px;
  height: 100px;
  background-color: #f3e5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.3s;
  position: relative;
}

.shape-hole:hover {
  transform: scale(1.1);
}

.shape {
  /* This class will inherit styles for the shape from getShapeStyle */
}

.message {
  font-size: 2rem;
  margin-top: 20px;
}

.progress {
  font-size: 1.5rem;
  margin-top: 20px;
}

.instruction {
  font-size: 2rem;
  margin-bottom: 20px;
}
</style>
